import {useMemo} from "react";
import { useLegendState as useState } from "@/hooks/useLegendState";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import {Badge} from "@/components/ui/badge";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {Switch} from "@/components/ui/switch";
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {useToast} from "@/hooks/use-toast";
import {queryClient} from "@/lib/queryClient";
import {schedulePrefetchKnowledgeBaseItem} from "@/lib/prefetchKnowledgeBaseItem";
import {knowledgeBaseService, type KnowledgeBaseItem} from "@/services/knowledgeBaseService";
import {ragService} from "@/services/ragService";
import { useSourceOfTruth } from "@/hooks/useSourceOfTruth";
import { toggleDocumentSourceOfTruth } from "@/services/sourceOfTruthService";
import {foldersService, type LibraryFolder} from "@/services/foldersService";
import {tagsService} from "@/services/tagsService";
import {invalidateKnowledgeBaseItem} from "@/queries/knowledgeBaseDocuments";
import type {Tag} from "@shared/schema";
import {
    Edit,
    Trash2,
    FileText,
    FileCheck,
    Lightbulb,
    Target,
    Users,
    Calendar,
    Code,
    Presentation,
    Link2,
    MoreVertical,
    FolderOpen,
    Type,
    Copy,
    Merge,
    Folder,
    X,
    Brain,
    Shield,
    Star,
    Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { EmbeddedSearchMark } from "@/components/EmbeddedSearchMark";
import {formatDistanceToNow} from "date-fns";
import {ThreadLinkingDialog} from "./ThreadLinkingDialog";
import {useLibrary} from "@/contexts/LibraryContext";
import {useDynamicTruncate, truncateText} from "@/hooks/use-dynamic-truncate";
import {EntityLockButton} from "./EntityLockButton";
import {useAuth} from "@/contexts/AuthContext";
import {useWorkspace} from "@/contexts/WorkspaceContext";
import type {Topic} from "@shared/schema";
import {
    effectiveItemActions,
    shouldRestrictEditorLinksToCurrentSharedSpace,
    type ItemOverrideMap,
    type SpaceMemberRole,
} from "@/services/sharedSpacePermissionService";
import { useCopyPublicDocumentLink } from "@/hooks/useCopyPublicDocumentLink";

const stripHtml = (html: string | null | undefined): string => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
};

const documentTypes = [
    {value: "note", label: "Note", icon: FileText, color: "hsl(220, 70%, 50%)", description: "Quick notes and ideas"},
    {
        value: "research",
        label: "Research",
        icon: FileCheck,
        color: "hsl(280, 67%, 55%)",
        description: "Research findings and analysis"
    },
    {
        value: "plan",
        label: "Plan",
        icon: Target,
        color: "hsl(142, 71%, 45%)",
        description: "Strategic plans and roadmaps"
    },
    {
        value: "idea",
        label: "Idea",
        icon: Lightbulb,
        color: "hsl(45, 93%, 47%)",
        description: "Creative ideas and concepts"
    },
    {
        value: "meeting",
        label: "Meeting Notes",
        icon: Users,
        color: "hsl(24, 95%, 53%)",
        description: "Meeting summaries and action items"
    },
    {
        value: "journal",
        label: "Journal",
        icon: Calendar,
        color: "hsl(0, 84%, 60%)",
        description: "Personal journal entries"
    },
    {
        value: "code",
        label: "Code Snippet",
        icon: Code,
        color: "hsl(271, 76%, 53%)",
        description: "Code examples and snippets"
    },
    {
        value: "presentation",
        label: "Presentation",
        icon: Presentation,
        color: "hsl(199, 89%, 48%)",
        description: "Presentation outlines and slides"
    },
];

interface DocumentItemProps {
    document: KnowledgeBaseItem;
    viewMode: "grid" | "list" | "compact" | "table";
    spaceId?: string;
    topics?: Topic[];
    embeddingStatusMap?: Record<string, boolean>;
    topicNameMap?: Record<string | number, string>;
    titleTruncate?: ReturnType<typeof useDynamicTruncate>;
    contentTruncate?: ReturnType<typeof useDynamicTruncate>;
    onRefresh?: () => void;
    onAttachRef?: (el: HTMLDivElement | null) => void;
    hasAttachedRef?: boolean;
    /** For compact view: max length for title truncation */
    compactTitleMaxLength?: number;
    /** Optional list of all documents for merge dialog */
    allDocuments?: KnowledgeBaseItem[];
    /** Optional callback when document is clicked (for handling locked documents) */
    onDocumentClick?: (documentId: string, isLocked?: boolean) => void;
    /** When true, document was unlocked this session (PIN verified) — show as unlocked in menu */
    isUnlockedInSession?: boolean;
    isSharedSpace?: boolean;
    spaceMemberRole?: SpaceMemberRole | null;
    spaceOwnerUserId?: string | null;
    itemOverrides?: ItemOverrideMap;
    isFavorite?: boolean;
    onToggleFavorite?: () => void;
}

export function DocumentItem({
    document: doc,
    viewMode,
    spaceId,
    topics: _topics = [],
    embeddingStatusMap = {},
    topicNameMap = {},
    titleTruncate,
    contentTruncate,
    onRefresh,
    onAttachRef,
    hasAttachedRef = false,
    compactTitleMaxLength: _compactTitleMaxLength = 22, // compact view uses CSS truncation; prop kept for API
    allDocuments = [],
    onDocumentClick,
    isUnlockedInSession = false,
    isSharedSpace = false,
    spaceMemberRole = null,
    spaceOwnerUserId = null,
    itemOverrides,
    isFavorite = false,
    onToggleFavorite,
}: DocumentItemProps) {
    const {toast} = useToast();
    const { copyPublicLink } = useCopyPublicDocumentLink();
    const { docIds: sourceOfTruthDocIds } = useSourceOfTruth();
    const isSourceOfTruth = sourceOfTruthDocIds.includes(doc.id);
    const {user} = useAuth();
    const {currentWorkspaceId} = useWorkspace();
    const {setSelectedDocumentId} = useLibrary();

    const itemActions =
        !spaceId
            ? {canView: true, canEdit: true, canDelete: true, canLink: true}
            : effectiveItemActions(
                  spaceMemberRole,
                  "document",
                  doc.id,
                  doc.userId,
                  user?.id,
                  spaceOwnerUserId,
                  itemOverrides,
              );

    const restrictEditorLinksToCurrentSpace = useMemo(
        () =>
            shouldRestrictEditorLinksToCurrentSharedSpace(
                isSharedSpace,
                spaceMemberRole,
                spaceOwnerUserId,
                user?.id
            ),
        [isSharedSpace, spaceMemberRole, spaceOwnerUserId, user?.id]
    );
    const [documentToLink, setDocumentToLink] = useState<KnowledgeBaseItem | null>(null);
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [renameValue, setRenameValue] = useState("");
    const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
    const [mergeTargetId, setMergeTargetId] = useState("");
    const [moveToFolderDialogOpen, setMoveToFolderDialogOpen] = useState(false);
    const [moveFolderTargetId, setMoveFolderTargetId] = useState<string | null>(null);

    // Get spaceIds from document (support both array and single value for backward compatibility)
    const getSpaceIds = (document: KnowledgeBaseItem): string[] => {
        const metadata = document.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        // Check for spaceIds array first (new format)
        if (Array.isArray(parsedMetadata.spaceIds)) {
            return parsedMetadata.spaceIds.filter((id: any) => id != null);
        }
        
        // Fallback to single spaceId in metadata (backward compatibility - more reliable than document.spaceId)
        if (parsedMetadata.spaceId) {
            return [String(parsedMetadata.spaceId)];
        }
        
        // Last fallback to document.spaceId (for very old documents)
        if (document.spaceId) {
            return [String(document.spaceId)];
        }
        
        return [];
    };

    // Get topicIds from document (support both array and single value for backward compatibility)
    const getTopicIds = (document: KnowledgeBaseItem): string[] => {
        const metadata = document.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        // Check for topicIds array first (new format)
        if (Array.isArray(parsedMetadata.topicIds)) {
            return parsedMetadata.topicIds.filter((id: any) => id != null);
        }
        
        // Fallback to single topicId in metadata (backward compatibility - more reliable than document.topicId)
        if (parsedMetadata.topicId) {
            return [String(parsedMetadata.topicId)];
        }
        
        return [];
    };

    // Get topic names for a document (supports multiple topics)
    const getTopicNames = (document: KnowledgeBaseItem): string[] => {
        const topicIds = getTopicIds(document);
        if (topicIds.length === 0) return [];
        
        return topicIds
            .map(id => {
                // Try to get from topicNameMap first (supports both string and number keys)
                const topicIdKey = typeof id === 'string' ? Number(id) : id;
                const nameFromMap = topicNameMap[topicIdKey] || topicNameMap[id];
                if (nameFromMap) return nameFromMap;
                
                // Fallback to topics array if available
                if (_topics && _topics.length > 0) {
                    const topic = _topics.find(t => String(t.id) === String(id));
                    if (topic) return topic.name;
                }
                
                return null;
            })
            .filter((name): name is string => name !== null);
    };

    // Get tagIds from document metadata (fallback to denormalized tagIds on item)
    const getTagIds = (document: KnowledgeBaseItem): string[] => {
        const metadata = document.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        const fromMeta = Array.isArray(parsedMetadata.tagIds) ? parsedMetadata.tagIds : null;
        const raw = fromMeta ?? document.tagIds ?? [];
        if (!Array.isArray(raw)) return [];
        return raw.filter((id: unknown) => id != null).map((id: unknown) => String(id));
    };

    // Fetch all tags for conversion between IDs and names
    const {data: allTags = []} = useQuery<Tag[]>({
        queryKey: ["/api/tags", user?.id],
        queryFn: async () => {
            if (!user) return [];
            return await tagsService.getTags();
        },
        enabled: !!user,
        staleTime: 60000,
    });

    // Convert tagIds to tag names
    const getTagNames = (document: KnowledgeBaseItem): string[] => {
        const tagIds = getTagIds(document);
        if (tagIds.length === 0 || allTags.length === 0) return [];
        
        return tagIds
            .map(id => {
                const tag = allTags.find(t => String(t.id) === String(id));
                return tag ? tag.name : null;
            })
            .filter((name): name is string => name !== null);
    };

    // Convert tag names to tagIds
    const convertTagNamesToIds = (tagNames: string[]): string[] => {
        if (tagNames.length === 0 || allTags.length === 0) return [];
        
        return tagNames
            .map(name => {
                const tag = allTags.find(t => t.name.toLowerCase() === name.toLowerCase());
                return tag ? tag.id : null;
            })
            .filter((id): id is string => id !== null);
    };

    const getDocumentTypeInfo = (type: string) => {
        return documentTypes.find(dt => dt.value === type) || documentTypes[0];
    };

    const qc = useQueryClient();
    const typeInfo = getDocumentTypeInfo(doc.type || "note");
    const IconComponent = typeInfo.icon;
    const isEmbedded = embeddingStatusMap[doc.id] || false;

    // Delete mutation
    const deleteDocumentMutation = useMutation({
        mutationFn: async (id: string) => {
            return await knowledgeBaseService.deleteKnowledgeBaseItem(id);
        },
        onSuccess: () => {
            onRefresh?.();
            toast({
                title: "Document Deleted",
                description: "Your document has been deleted",
            });
        },
        onError: () => {
            toast({
                title: "Delete Failed",
                description: "There was an error deleting your document",
                variant: "destructive",
            });
        },
    });

    // Embed for search
    const handleEmbedForSearch = async () => {
        try {
            await ragService.embedDocument(doc.id);
            toast({title: "Embedded for search", description: `"${doc.title}" is now searchable by meaning.`});
            queryClient.invalidateQueries({queryKey: ["embedding-status-docgrid"]});
            onRefresh?.();
        } catch (e) {
            toast({title: "Embed failed", description: e instanceof Error ? e.message : "Could not embed document.", variant: "destructive"});
        }
    };

    // Re-embed for search
    const handleReembedForSearch = async () => {
        try {
            await ragService.reembedDocument(doc.id);
            toast({title: "Re-embedded for search", description: `"${doc.title}" has been updated for semantic search.`});
            queryClient.invalidateQueries({queryKey: ["embedding-status-docgrid"]});
            onRefresh?.();
        } catch (e) {
            toast({title: "Re-embed failed", description: e instanceof Error ? e.message : "Could not re-embed document.", variant: "destructive"});
        }
    };

    const handleRemoveFromSearch = async () => {
        try {
            await ragService.deleteDocumentEmbeddings(doc.id);
            toast({title: "Removed from search", description: `"${doc.title}" is no longer embedded.`});
            queryClient.invalidateQueries({queryKey: ["embedding-status-docgrid"]});
            queryClient.invalidateQueries({queryKey: ["embedding-status"]});
            onRefresh?.();
        } catch (e) {
            toast({title: "Remove failed", description: e instanceof Error ? e.message : "Could not remove embeddings.", variant: "destructive"});
        }
    };

    // Toggle include in RAG
    const handleToggleIncludeInRag = async (value: boolean) => {
        try {
            await knowledgeBaseService.updateKnowledgeBaseItem(doc.id, {includeInRag: value});
            toast({title: value ? "Included in RAG" : "Excluded from RAG", description: `"${doc.title}" is ${value ? "in" : "out of"} RAG retrieval.`});
            onRefresh?.();
        } catch (e) {
            toast({title: "Update failed", description: e instanceof Error ? e.message : "Could not update.", variant: "destructive"});
        }
    };

    const handleToggleSourceOfTruth = async () => {
        try {
            const added = await toggleDocumentSourceOfTruth(doc.id);
            toast({
                title: added ? "Source of Truth" : "Removed from canon",
                description: added
                    ? `"${doc.title}" is always included in every chat.`
                    : `"${doc.title}" is no longer in your global canon.`,
            });
            onRefresh?.();
        } catch (e) {
            toast({
                title: "Update failed",
                description: e instanceof Error ? e.message : "Could not update.",
                variant: "destructive",
            });
        }
    };

    // Fetch folders for move to folder dialog (scoped by workspace or personal)
    const {data: folders = []} = useQuery<LibraryFolder[]>({
        queryKey: ["/api/library-folders", user?.id, currentWorkspaceId ?? "personal"],
        queryFn: async () => {
            if (!user) return [];
            return await foldersService.getFolders(currentWorkspaceId ?? null);
        },
        enabled: !!user && moveToFolderDialogOpen,
        staleTime: 60000,
    });

    // Open edit (sets selectedDocumentId to open in RightSidebar)
    const handleOpenEdit = () => {
        if (onDocumentClick) {
            onDocumentClick(doc.id, doc.isLocked);
        } else {
            setSelectedDocumentId(doc.id);
        }
    };

    // Handle linking to space/topic
    const handleLinkToSpaceTopic = async () => {
        // Check if document needs migration from old format to new array format
        const metadata = doc.metadata || {};
        const parsedMetadata = typeof metadata === 'string' ? (() => {
            try { return JSON.parse(metadata); } catch { return {}; }
        })() : metadata;
        
        const hasSpaceIdsArray = Array.isArray(parsedMetadata.spaceIds);
        const hasOldSpaceId = doc.spaceId && !hasSpaceIdsArray;
        
        const hasTopicIdsArray = Array.isArray(parsedMetadata.topicIds);
        const hasOldTopicId = doc.topicId && !hasTopicIdsArray;
        
        let needsMigration = false;
        let migratedSpaceIds: string[] = [];
        let migratedTopicIds: string[] = [];
        
        // Migrate spaceId to spaceIds if needed
        if (hasOldSpaceId) {
            needsMigration = true;
            migratedSpaceIds = [String(doc.spaceId)];
        } else if (hasSpaceIdsArray) {
            migratedSpaceIds = parsedMetadata.spaceIds;
        } else {
            migratedSpaceIds = getSpaceIds(doc);
        }
        
        // Migrate topicId to topicIds if needed
        if (hasOldTopicId) {
            needsMigration = true;
            migratedTopicIds = [String(doc.topicId)];
        } else if (hasTopicIdsArray) {
            migratedTopicIds = parsedMetadata.topicIds;
        } else {
            migratedTopicIds = getTopicIds(doc);
        }
        
        // Create migrated document structure for the modal (immediate, no API wait)
        const migratedDoc: KnowledgeBaseItem = {
            ...doc,
            metadata: {
                ...parsedMetadata,
                spaceIds: migratedSpaceIds,
                spaceId: null, // Clear old spaceId in metadata
                topicIds: migratedTopicIds,
                topicId: null, // Clear old topicId in metadata
                tagIds: getTagIds(doc) // Preserve existing tagIds
            },
            spaceId: null, // Clear old spaceId on document level
            topicId: null // Clear old topicId on document level
        };
        
        // Set the migrated document for the modal immediately
        setDocumentToLink(migratedDoc);
        
        // Migrate the document in the background (update database) if needed
        if (needsMigration) {
            try {
                const updateData: any = {};
                if (hasOldSpaceId) {
                    updateData.spaceIds = migratedSpaceIds;
                }
                if (hasOldTopicId) {
                    updateData.topicIds = migratedTopicIds;
                }
                
                await knowledgeBaseService.updateKnowledgeBaseItem(doc.id, updateData);
                
                // Optimistically update the cache
                queryClient.setQueriesData<KnowledgeBaseItem[]>(
                    {queryKey: ["/api/knowledgebase"], exact: false},
                    (oldData) => {
                        if (!oldData) return oldData;
                        return oldData.map(d => {
                            if (d.id === doc.id) {
                                const updatedMetadata = typeof d.metadata === 'string' 
                                    ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })()
                                    : (d.metadata || {});
                                return {
                                    ...d,
                                    metadata: {
                                        ...updatedMetadata,
                                        spaceIds: migratedSpaceIds,
                                        spaceId: null,
                                        topicIds: migratedTopicIds,
                                        topicId: null
                                    },
                                    spaceId: null,
                                    topicId: null
                                };
                            }
                            return d;
                        });
                    }
                );
                
                // Refetch to ensure consistency
                await queryClient.refetchQueries({queryKey: ["/api/knowledgebase"], exact: false});
                onRefresh?.();
            } catch (error: any) {
                console.error("Failed to migrate document to new format:", error);
                // Modal already has the migrated structure, so it will still work correctly
            }
        }
    };

    // Handle multiple spaces update from linking dialog
    const handleUpdateSpaces = async (selectedSpaceIds: string[]) => {
        try {
            // Get current topicIds - don't reset them, let ThreadLinkingDialog handle filtering
            const currentTopicIds = getTopicIds(doc);
            
            // Update spaces only - topics will be updated separately via handleUpdateTopics
            await knowledgeBaseService.updateKnowledgeBaseItem(
                doc.id,
                {
                    spaceIds: selectedSpaceIds, // Store as array
                    // Don't update topicIds here - let the dialog handle it through onUpdateTopics
                },
                spaceId ?? null
            );
            
            // Optimistically update the cache
            queryClient.setQueriesData<KnowledgeBaseItem[]>(
                {queryKey: ["/api/knowledgebase"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(d => {
                        if (String(d.id) === String(doc.id)) {
                            const metadata = typeof d.metadata === 'string' 
                                ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })()
                                : (d.metadata || {});
                            return {
                                ...d, 
                                spaceId: selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null, // Keep for backward compatibility
                                spaceIds: selectedSpaceIds, // New array format
                                metadata: {
                                    ...metadata,
                                    spaceIds: selectedSpaceIds,
                                    spaceId: null, // Clear old spaceId when using spaceIds array
                                    // Keep existing topicIds - they'll be updated separately if needed
                                    topicIds: currentTopicIds,
                                    topicId: null // Clear topicId when using topicIds array
                                }
                            };
                        }
                        return d;
                    });
                }
            );
            
            // Update documentToLink for the modal - preserve current topics
            const updatedDoc: KnowledgeBaseItem = {
                ...doc,
                spaceId: selectedSpaceIds.length > 0 ? selectedSpaceIds[0] : null,
                spaceIds: selectedSpaceIds,
                metadata: {
                    ...(typeof doc.metadata === 'string' ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })() : (doc.metadata || {})),
                    spaceIds: selectedSpaceIds,
                    spaceId: null,
                    topicIds: currentTopicIds, // Preserve current topics
                    topicId: null
                }
            };
            setDocumentToLink(updatedDoc);

            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
            if (selectedSpaceIds.length > 0) {
                // Invalidate topics for all selected spaces
                selectedSpaceIds.forEach(spaceId => {
                    queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
                });
            }
            onRefresh?.();
            toast({
                title: "Spaces updated",
                description: selectedSpaceIds.length > 0 
                    ? `Document has been linked to ${selectedSpaceIds.length} space${selectedSpaceIds.length > 1 ? 's' : ''}.`
                    : "Document has been unlinked from all spaces.",
            });
        } catch (error: any) {
            queryClient.refetchQueries({queryKey: ["/api/knowledgebase"], exact: false});
            console.error("Failed to update document spaces:", error);
            toast({
                title: "Update failed",
                description: error.message || "Failed to update document spaces",
                variant: "destructive",
            });
        }
    };

    // Handle multiple topics update from linking dialog (for documents)
    const handleUpdateTopics = async (selectedTopicIds: string[]) => {
        try {
            const updated = await knowledgeBaseService.updateKnowledgeBaseItem(doc.id, {topicIds: selectedTopicIds}, spaceId ?? null);
            if (!updated) {
                throw new Error("The topic link was not saved (document not found or no permission).");
            }

            // Optimistically update the cache
            queryClient.setQueriesData<KnowledgeBaseItem[]>(
                {queryKey: ["/api/knowledgebase"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(d => {
                        if (d.id === doc.id) {
                            const updated = {...d};
                            // Update metadata with topicIds
                            const metadata = typeof d.metadata === 'string' 
                                ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })()
                                : (d.metadata || {});
                            updated.metadata = {
                                ...metadata,
                                topicIds: selectedTopicIds,
                                // Set topicId to null when using topicIds array to avoid dual values
                                topicId: null
                            };
                            return updated;
                        }
                        return d;
                    });
                }
            );

            // Refetch to get the latest document data
            await queryClient.refetchQueries({queryKey: ["/api/knowledgebase"], exact: false});
            
            // Update documentToLink with the latest data
            const updatedDoc: KnowledgeBaseItem = {
                ...doc,
                metadata: {
                    ...(typeof doc.metadata === 'string' ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })() : (doc.metadata || {})),
                    topicIds: selectedTopicIds,
                    topicId: null
                }
            };
            setDocumentToLink(updatedDoc);
            
            if (spaceId) {
                queryClient.invalidateQueries({queryKey: ["/api/spaces", spaceId, "topics"]});
            }
            queryClient.invalidateQueries({queryKey: ["/api/topics"]});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary"]});
            onRefresh?.();
            toast({
                title: "Document linked",
                description: selectedTopicIds.length > 0 
                    ? `Document has been linked to ${selectedTopicIds.length} topic${selectedTopicIds.length > 1 ? 's' : ''}`
                    : "Document has been unlinked from all topics",
            });
        } catch (error: any) {
            queryClient.refetchQueries({queryKey: ["/api/knowledgebase"], exact: false});
            console.error("Failed to update document topics:", error);
            toast({
                title: "Link failed",
                description: error.message || "Failed to update document topics",
                variant: "destructive",
            });
        }
    };

    // Handle tags update from linking dialog (for documents)
    const handleUpdateTags = async (selectedTagNames: string[]) => {
        try {
            // Convert tag names to tagIds
            const tagIds = convertTagNamesToIds(selectedTagNames);
            
            // Update document with tagIds in metadata
            await knowledgeBaseService.updateKnowledgeBaseItem(doc.id, {tagIds});
            
            // Optimistically update the cache
            queryClient.setQueriesData<KnowledgeBaseItem[]>(
                {queryKey: ["/api/knowledgebase"], exact: false},
                (oldData) => {
                    if (!oldData) return oldData;
                    return oldData.map(d => {
                        if (d.id === doc.id) {
                            const updated = {...d};
                            const metadata = typeof d.metadata === 'string' 
                                ? (() => { try { return JSON.parse(d.metadata); } catch { return {}; } })()
                                : (d.metadata || {});
                            updated.metadata = {
                                ...metadata,
                                tagIds: tagIds
                            };
                            updated.tagIds = tagIds;
                            return updated;
                        }
                        return d;
                    });
                }
            );

            await queryClient.invalidateQueries({
                queryKey: ["knowledgebase-summary", user?.id],
            });
            await invalidateKnowledgeBaseItem(queryClient, doc.id);
            
            // Update documentToLink with the latest data - preserve current spaces and topics
            const currentSpaceIds = getSpaceIds(doc);
            const currentTopicIds = getTopicIds(doc);
            const updatedDoc: KnowledgeBaseItem = {
                ...doc,
                metadata: {
                    ...(typeof doc.metadata === 'string' ? (() => { try { return JSON.parse(doc.metadata); } catch { return {}; } })() : (doc.metadata || {})),
                    spaceIds: currentSpaceIds, // Preserve current spaces
                    topicIds: currentTopicIds, // Preserve current topics
                    tagIds: tagIds
                }
            };
            setDocumentToLink(updatedDoc);
            
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            onRefresh?.();
            toast({
                title: "Tags updated",
                description: selectedTagNames.length > 0 
                    ? `Document has been tagged with ${selectedTagNames.length} tag${selectedTagNames.length > 1 ? 's' : ''}`
                    : "All tags have been removed from the document",
            });
        } catch (error: any) {
            queryClient.refetchQueries({queryKey: ["/api/knowledgebase"], exact: false});
            console.error("Failed to update document tags:", error);
            toast({
                title: "Update failed",
                description: error.message || "Failed to update document tags",
                variant: "destructive",
            });
        }
    };

    // Rename mutation
    const renameMutation = useMutation({
        mutationFn: async ({id, title}: {id: string; title: string}) =>
            knowledgeBaseService.updateKnowledgeBaseItem(id, {title}),
        onSuccess: () => {
            toast({title: "Document renamed", description: "The document title has been updated."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            setRenameDialogOpen(false);
            setRenameValue("");
            onRefresh?.();
        },
        onError: (error) => {
            toast({title: "Error renaming", description: error instanceof Error ? error.message : "Failed to rename", variant: "destructive"});
        },
    });

    // Duplicate mutation
    const duplicateMutation = useMutation({
        mutationFn: (id: string) => knowledgeBaseService.duplicateDocument(id),
        onSuccess: () => {
            toast({title: "Document duplicated", description: "A copy has been created."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            onRefresh?.();
        },
        onError: (error) => {
            toast({title: "Error duplicating", description: error instanceof Error ? error.message : "Failed to duplicate", variant: "destructive"});
        },
    });

    // Merge mutation
    const mergeMutation = useMutation({
        mutationFn: async ({sourceId, targetId}: {sourceId: string; targetId: string}) =>
            knowledgeBaseService.mergeDocuments(sourceId, targetId),
        onSuccess: () => {
            toast({title: "Documents merged", description: "The documents have been combined."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            setMergeDialogOpen(false);
            setMergeTargetId("");
            onRefresh?.();
        },
        onError: (error) => {
            toast({title: "Error merging", description: error instanceof Error ? error.message : "Failed to merge", variant: "destructive"});
        },
    });

    // Move to folder mutation
    const moveToFolderMutation = useMutation({
        mutationFn: async ({itemId, folderId}: {itemId: string; folderId: string | null}) => {
            return knowledgeBaseService.updateKnowledgeBaseItem(itemId, {folderId});
        },
        onSuccess: () => {
            toast({title: "Item moved", description: "The item has been moved to the folder."});
            queryClient.invalidateQueries({queryKey: ["knowledgebase-summary", user?.id]});
            queryClient.invalidateQueries({queryKey: ["/api/library-folders"]});
            queryClient.invalidateQueries({queryKey: ["/api/knowledgebase"]});
            setMoveToFolderDialogOpen(false);
            setMoveFolderTargetId(null);
            onRefresh?.();
        },
        onError: (error) => {
            toast({title: "Error moving item", description: error instanceof Error ? error.message : "Failed to move", variant: "destructive"});
        },
    });

    // Handlers
    const handleRename = () => {
        setRenameValue(doc.title || "");
        setRenameDialogOpen(true);
    };

    const handleConfirmRename = () => {
        if (renameValue.trim()) {
            renameMutation.mutate({id: doc.id, title: renameValue.trim()});
        }
    };

    const handleDuplicate = () => {
        duplicateMutation.mutate(doc.id);
    };

    const handleMerge = () => {
        setMergeTargetId("");
        setMergeDialogOpen(true);
    };

    const handleConfirmMerge = () => {
        if (mergeTargetId) {
            mergeMutation.mutate({sourceId: doc.id, targetId: mergeTargetId});
        }
    };

    const handleMoveToFolder = () => {
        setMoveFolderTargetId(doc.folderId || null);
        setMoveToFolderDialogOpen(true);
    };

    const handleConfirmMoveToFolder = () => {
        moveToFolderMutation.mutate({itemId: doc.id, folderId: moveFolderTargetId});
    };

    const handleRemoveFromFolder = () => {
        moveToFolderMutation.mutate({itemId: doc.id, folderId: null});
    };

    // Get documents for merge (exclude current document)
    const documentsForMerge = allDocuments.filter((item) => item.id !== doc.id);

    // Helper function to truncate title
    const truncateTitle = (title: string, maxLength: number): string => {
        if (!title) return "";
        return title.length <= maxLength ? title : title.slice(0, maxLength) + "...";
    };

    // Render dropdown menu (same for all views)
    const renderDropdownMenu = () => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button
                    variant="ghost"
                    size="icon"
                    className={
                        viewMode === "grid" 
                            ? "h-7 w-7" 
                            : viewMode === "compact"
                            ? "h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            : viewMode === "table"
                            ? "h-8 w-8"
                            : "h-7 w-7 p-0 opacity-60 group-hover:opacity-100 transition-opacity"
                    }
                    data-testid={`button-document-menu-${viewMode}-${doc.id}`}
                >
                    <MoreVertical className={
                        viewMode === "grid" ? "h-4 w-4" 
                        : viewMode === "compact" ? "h-3 w-3" 
                        : viewMode === "table" ? "h-4 w-4"
                        : "h-3.5 w-3.5"
                    }/>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit();
                    }}
                    data-testid={`menu-edit-doc-${viewMode}-${doc.id}`}
                >
                    <Edit className="h-4 w-4 mr-2"/>
                    Edit
                </DropdownMenuItem>
                {onToggleFavorite && (
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleFavorite();
                        }}
                    >
                        <Star
                            className={cn(
                                "h-4 w-4 mr-2",
                                isFavorite && "fill-amber-500 text-amber-500"
                            )}
                        />
                        {isFavorite ? "Remove from favorites" : "Add to favorites"}
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRename();
                    }}
                    data-testid={`menu-rename-doc-${viewMode}-${doc.id}`}
                >
                    <Type className="h-4 w-4 mr-2"/>
                    Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleDuplicate();
                    }}
                    data-testid={`menu-duplicate-doc-${viewMode}-${doc.id}`}
                    disabled={duplicateMutation.isPending}
                >
                    <Copy className="h-4 w-4 mr-2"/>
                    Duplicate
                </DropdownMenuItem>
                {itemActions.canEdit && (
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        void copyPublicLink(doc.id);
                    }}
                    data-testid={`menu-copy-public-link-doc-${viewMode}-${doc.id}`}
                >
                    <Globe className="h-4 w-4 mr-2"/>
                    Copy public link
                </DropdownMenuItem>
                )}
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMerge();
                    }}
                    data-testid={`menu-merge-doc-${viewMode}-${doc.id}`}
                >
                    <Merge className="h-4 w-4 mr-2"/>
                    Merge into...
                </DropdownMenuItem>
                {itemActions.canLink && (
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleLinkToSpaceTopic();
                    }}
                    data-testid={`menu-link-space-topic-doc-${viewMode}-${doc.id}`}
                >
                    <Link2 className="h-4 w-4 mr-2"/>
                    Link Space/Topic
                </DropdownMenuItem>
                )}
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleSourceOfTruth();
                    }}
                    data-testid={`menu-ssot-doc-${viewMode}-${doc.id}`}
                >
                    <Shield className={`h-4 w-4 mr-2 ${isSourceOfTruth ? "text-primary" : ""}`}/>
                    {isSourceOfTruth ? "Remove from Source of Truth" : "Mark as Source of Truth"}
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                {isEmbedded ? (
                    <>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleReembedForSearch();
                        }}
                        data-testid={`menu-reembed-doc-${viewMode}-${doc.id}`}
                    >
                        <Brain className="h-4 w-4 mr-2"/>
                        Re-embed for search
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            void handleRemoveFromSearch();
                        }}
                        data-testid={`menu-remove-embed-doc-${viewMode}-${doc.id}`}
                    >
                        <X className="h-4 w-4 mr-2"/>
                        Remove from search
                    </DropdownMenuItem>
                    </>
                ) : (
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEmbedForSearch();
                        }}
                        data-testid={`menu-embed-doc-${viewMode}-${doc.id}`}
                    >
                        <Brain className="h-4 w-4 mr-2"/>
                        Embed for search
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center justify-between gap-4 cursor-default"
                    onClick={(e) => e.stopPropagation()}
                >
                    <span>Include</span>
                    <Switch
                        checked={doc.includeInRag !== false}
                        onCheckedChange={(checked) => handleToggleIncludeInRag(checked)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`switch-doc-include-rag-${viewMode}-${doc.id}`}
                    />
                </DropdownMenuItem>
                <DropdownMenuSeparator/>
                <div className="px-2 py-1.5" onClick={(e) => e.stopPropagation()}>
                    <EntityLockButton
                        entityType="document"
                        entityId={doc.id}
                        isLocked={doc.isLocked === true && !isUnlockedInSession}
                        size="menu"
                        customTitle={doc.isLocked ? "Unlock document" : "Lock document"}
                    />
                </div>
                <DropdownMenuSeparator/>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        handleMoveToFolder();
                    }}
                    data-testid={`menu-move-to-folder-doc-${viewMode}-${doc.id}`}
                >
                    <Folder className="h-4 w-4 mr-2"/>
                    Move to Folder...
                </DropdownMenuItem>
                {doc.folderId && (
                    <DropdownMenuItem
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromFolder();
                        }}
                        data-testid={`menu-remove-from-folder-doc-${viewMode}-${doc.id}`}
                        disabled={moveToFolderMutation.isPending}
                    >
                        <X className="h-4 w-4 mr-2"/>
                        Remove from Folder
                    </DropdownMenuItem>
                )}
                <DropdownMenuSeparator/>
                <DropdownMenuItem
                    onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Are you sure you want to delete "${doc.title || "Untitled Document"}"? This action cannot be undone.`)) {
                            deleteDocumentMutation.mutate(doc.id);
                        }
                    }}
                    className="text-destructive focus:text-destructive"
                    data-testid={`menu-delete-doc-${viewMode}-${doc.id}`}
                    disabled={deleteDocumentMutation.isPending}
                >
                    <Trash2 className="h-4 w-4 mr-2"/>
                    Delete
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    // Grid view
    if (viewMode === "grid") {
        return (
            <>
                <Card key={doc.id} className="hover-elevate" style={{overflow: 'hidden'}}
                      data-testid={`document-card-${doc.id}`}
                      onPointerEnter={() => schedulePrefetchKnowledgeBaseItem(qc, doc.id)}>
                    <CardHeader className="gap-2 space-y-0 pb-2">
                        <div className="flex items-center justify-between">
                            <div
                                className="p-2 rounded-md"
                                style={{backgroundColor: `${typeInfo.color}15`}}
                            >
                                <IconComponent className="h-4 w-4" style={{color: typeInfo.color}}/>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                                {isSourceOfTruth && (
                                    <Badge variant="secondary" className="text-[10px] h-5 gap-0.5" title="Source of Truth">
                                        <Shield className="h-3 w-3" />
                                        Canon
                                    </Badge>
                                )}
                                {renderDropdownMenu()}
                            </div>
                        </div>
                        <CardTitle 
                            className="text-base line-clamp-2 cursor-pointer hover:text-primary transition-colors inline-flex items-start gap-1.5"
                            onClick={handleOpenEdit}
                        >
                            <span className="line-clamp-2">{doc.title}</span>
                            {isEmbedded && <EmbeddedSearchMark className="mt-0.5" />}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {doc.content && (
                            <CardDescription className="line-clamp-3 text-xs">
                                {stripHtml(doc.content)}
                            </CardDescription>
                        )}
                        {(() => {
                            const topicNames = getTopicNames(doc);
                            if (topicNames.length === 0) return null;
                            
                            // Show first topic, and if there are more, show a count
                            return (
                                <div className="flex flex-wrap gap-1">
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 gap-1 w-fit">
                                        <FolderOpen className="w-2.5 h-2.5"/>
                                        <span className="truncate">{topicNames[0]}</span>
                                    </Badge>
                                    {topicNames.length > 1 && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                            +{topicNames.length - 1}
                                        </Badge>
                                    )}
                                </div>
                            );
                        })()}
                        <div className="text-xs text-muted-foreground">
                            {doc.updatedAt ? formatDistanceToNow(new Date(doc.updatedAt), {addSuffix: true}) : "Unknown"}
                        </div>
                    </CardContent>
                </Card>

                {/* Linking Dialog */}
                {documentToLink && (
                    <ThreadLinkingDialog
                        open={!!documentToLink}
                        onOpenChange={(open) => !open && setDocumentToLink(null)}
                        title="Link to Space/Topic"
                        description="Connect this document to spaces, topics, and tags for better organization"
                        linkedSpaceIds={getSpaceIds(documentToLink)}
                        topicId={null}
                        topicIds={getTopicIds(documentToLink)}
                        tags={getTagNames(documentToLink)}
                        allowMultipleTopics={true}
                        onUpdateSpaces={handleUpdateSpaces}
                        onUpdateTopics={handleUpdateTopics}
                        onUpdateTags={handleUpdateTags}
                        editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                    />
                )}
            </>
        );
    }

    // List view
    if (viewMode === "list") {
        return (
            <>
                <div
                    key={doc.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover-elevate group w-full"
                    onPointerEnter={() => schedulePrefetchKnowledgeBaseItem(qc, doc.id)}
                    data-testid={`document-row-${doc.id}`}
                >
                    {/* Icon */}
                    <div
                        className="p-1.5 rounded-md flex-shrink-0"
                        style={{backgroundColor: `${typeInfo.color}15`}}
                    >
                        <IconComponent className="h-4 w-4" style={{color: typeInfo.color}}/>
                    </div>

                    {/* Title and content preview */}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                className="flex-1 min-w-0 overflow-hidden"
                                ref={(el) => {
                                    if (onAttachRef && el && !hasAttachedRef) {
                                        onAttachRef(el);
                                    }
                                }}
                            >
                                <div className="flex flex-col min-w-0 flex-1">
                                    <div
                                        className="font-medium text-sm truncate cursor-pointer hover:text-primary transition-colors flex items-center gap-1.5"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenEdit();
                                        }}
                                        title="Click to edit document"
                                        data-testid={`document-title-${doc.id}`}
                                    >
                                        <span className="truncate">
                                            {titleTruncate ? truncateText(doc.title || '', titleTruncate.maxLength) : doc.title}
                                        </span>
                                        {isEmbedded && <EmbeddedSearchMark />}
                                    </div>

                                    {doc.content && (
                                        <div className="text-xs text-muted-foreground truncate">
                                            {contentTruncate ? truncateText(stripHtml(doc.content), contentTruncate.maxLength) : stripHtml(doc.content)}
                                        </div>
                                    )}
                                    {(() => {
                                        const topicNames = getTopicNames(doc);
                                        if (topicNames.length === 0) return null;
                                        
                                        // Show first topic, and if there are more, show a count
                                        return (
                                            <div className="mt-0.5 flex flex-wrap gap-1">
                                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 gap-1">
                                                    <FolderOpen className="w-2.5 h-2.5"/>
                                                    <span className="truncate">{topicNames[0]}</span>
                                                </Badge>
                                                {topicNames.length > 1 && (
                                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                                        +{topicNames.length - 1}
                                                    </Badge>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                            <div className="flex flex-col">
                                <span>{doc.title}</span>
                                {(doc.content || doc.updatedAt) && (
                                    <span className="text-xs text-muted-foreground">
                                        {doc.content && stripHtml(doc.content).length > 0
                                            ? stripHtml(doc.content)
                                            : doc.updatedAt
                                                ? formatDistanceToNow(new Date(doc.updatedAt), {addSuffix: true})
                                                : "No content"
                                        }
                                    </span>
                                )}
                            </div>
                        </TooltipContent>
                    </Tooltip>

                    {/* Status & Meta */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="outline" className="text-xs">
                            {typeInfo.label}
                        </Badge>
                        <div className="text-xs text-muted-foreground w-20 text-right flex-shrink-0">
                            {doc.updatedAt ? formatDistanceToNow(new Date(doc.updatedAt), {addSuffix: true}) : "Unknown"}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="w-8 flex-shrink-0 flex justify-end">
                        {renderDropdownMenu()}
                    </div>
                </div>

                {/* Linking Dialog */}
                {documentToLink && (
                    <ThreadLinkingDialog
                        open={!!documentToLink}
                        onOpenChange={(open) => !open && setDocumentToLink(null)}
                        title="Link to Space/Topic"
                        description="Connect this document to spaces, topics, and tags for better organization"
                        linkedSpaceIds={getSpaceIds(documentToLink)}
                        topicId={null}
                        topicIds={getTopicIds(documentToLink)}
                        tags={getTagNames(documentToLink)}
                        allowMultipleTopics={true}
                        onUpdateSpaces={handleUpdateSpaces}
                        onUpdateTopics={handleUpdateTopics}
                        onUpdateTags={handleUpdateTags}
                        editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                    />
                )}

                {/* Rename Dialog */}
                <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Rename Document</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="rename-title-list">New Title</Label>
                                <Input
                                    id="rename-title-list"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    placeholder="Enter new title..."
                                    onKeyDown={(e) => e.key === "Enter" && handleConfirmRename()}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmRename} disabled={renameMutation.isPending || !renameValue.trim()}>
                                {renameMutation.isPending ? "Renaming..." : "Rename"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Merge Dialog */}
                <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Merge Document</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Select a document to merge &quot;{doc.title}&quot; into. The content will be appended to the target document, and this document will be deleted.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="merge-target-list">Target Document</Label>
                                <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                                    <SelectTrigger id="merge-target-list">
                                        <SelectValue placeholder="Select a document..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {documentsForMerge.map((item) => (
                                            <SelectItem key={item.id} value={item.id}>{item.title ?? "Untitled"}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmMerge} disabled={mergeMutation.isPending || !mergeTargetId}>
                                {mergeMutation.isPending ? "Merging..." : "Merge"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Move to Folder Dialog */}
                <Dialog open={moveToFolderDialogOpen} onOpenChange={setMoveToFolderDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Move to Folder</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Move &quot;{doc.title}&quot; to a folder.
                            </p>
                            <div className="space-y-2">
                                <Label>Folder</Label>
                                <Select
                                    value={moveFolderTargetId ?? "none"}
                                    onValueChange={(v) => setMoveFolderTargetId(v === "none" ? null : v)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a folder..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No folder (unfiled)</SelectItem>
                                        {folders.map((f) => (
                                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setMoveToFolderDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmMoveToFolder} disabled={moveToFolderMutation.isPending}>
                                {moveToFolderMutation.isPending ? "Moving..." : "Move"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    // Table view (returns just the dropdown menu for table cells)
    if (viewMode === "table") {
        return (
            <>
                {renderDropdownMenu()}
                {/* Dialogs */}
                {documentToLink && (
                    <ThreadLinkingDialog
                        open={!!documentToLink}
                        onOpenChange={(open) => !open && setDocumentToLink(null)}
                        title="Link to Space/Topic"
                        description="Connect this document to spaces, topics, and tags for better organization"
                        linkedSpaceIds={getSpaceIds(documentToLink)}
                        topicId={null}
                        topicIds={getTopicIds(documentToLink)}
                        tags={getTagNames(documentToLink)}
                        allowMultipleTopics={true}
                        onUpdateSpaces={handleUpdateSpaces}
                        onUpdateTopics={handleUpdateTopics}
                        onUpdateTags={handleUpdateTags}
                        editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                    />
                )}
                <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Rename Document</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="rename-title-table">New Title</Label>
                                <Input
                                    id="rename-title-table"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    placeholder="Enter new title..."
                                    onKeyDown={(e) => e.key === "Enter" && handleConfirmRename()}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmRename} disabled={renameMutation.isPending || !renameValue.trim()}>
                                {renameMutation.isPending ? "Renaming..." : "Rename"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Merge Document</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Select a document to merge &quot;{doc.title}&quot; into. The content will be appended to the target document, and this document will be deleted.
                            </p>
                            <div className="space-y-2">
                                <Label htmlFor="merge-target-table">Target Document</Label>
                                <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                                    <SelectTrigger id="merge-target-table">
                                        <SelectValue placeholder="Select a document..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {documentsForMerge.map((item) => (
                                            <SelectItem key={item.id} value={item.id}>{item.title ?? "Untitled"}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmMerge} disabled={mergeMutation.isPending || !mergeTargetId}>
                                {mergeMutation.isPending ? "Merging..." : "Merge"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                <Dialog open={moveToFolderDialogOpen} onOpenChange={setMoveToFolderDialogOpen}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle>Move to Folder</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <p className="text-sm text-muted-foreground">
                                Move &quot;{doc.title}&quot; to a folder.
                            </p>
                            <div className="space-y-2">
                                <Label>Folder</Label>
                                <Select
                                    value={moveFolderTargetId ?? "none"}
                                    onValueChange={(v) => setMoveFolderTargetId(v === "none" ? null : v)}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a folder..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No folder (unfiled)</SelectItem>
                                        {folders.map((f) => (
                                            <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setMoveToFolderDialogOpen(false)}>Cancel</Button>
                            <Button onClick={handleConfirmMoveToFolder} disabled={moveToFolderMutation.isPending}>
                                {moveToFolderMutation.isPending ? "Moving..." : "Move"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </>
        );
    }

    // Compact view (for TopicsManagement and similar compact displays)
    return (
        <>
            <div
                key={doc.id}
                className="grid grid-cols-[auto_1fr_auto] gap-2 items-center px-2 py-1 rounded-md hover:bg-accent/50 transition-colors min-w-0 group cursor-pointer text-xs w-full max-w-full"
                onClick={handleOpenEdit}
                data-testid={`document-${doc.id}`}
            >
                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"/>
                <div className="min-w-0 overflow-hidden flex items-center gap-1.5">
                    <span className="truncate block" title={doc.title || "Untitled Document"}>
                        {doc.title || "Untitled Document"}
                    </span>
                    {isEmbedded && <EmbeddedSearchMark className="h-3 w-3" />}
                </div>
                <div className="flex items-center shrink-0 justify-end">
                    {renderDropdownMenu()}
                </div>
            </div>

            {/* Linking Dialog */}
            {documentToLink && (
                <ThreadLinkingDialog
                    open={!!documentToLink}
                    onOpenChange={(open) => !open && setDocumentToLink(null)}
                    title="Link to Space/Topic"
                    description="Connect this document to spaces, topics, and tags for better organization"
                    linkedSpaceIds={getSpaceIds(documentToLink)}
                    topicId={null}
                    topicIds={getTopicIds(documentToLink)}
                    tags={getTagNames(documentToLink)}
                    allowMultipleTopics={true}
                    onUpdateSpaces={handleUpdateSpaces}
                    onUpdateTopics={handleUpdateTopics}
                    onUpdateTags={handleUpdateTags}
                    editorLinksRestrictedToSpaceId={restrictEditorLinksToCurrentSpace && spaceId ? spaceId : null}
                />
            )}

            {/* Rename Dialog */}
            <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Rename Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="rename-title-compact">New Title</Label>
                            <Input
                                id="rename-title-compact"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                placeholder="Enter new title..."
                                onKeyDown={(e) => e.key === "Enter" && handleConfirmRename()}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirmRename} disabled={renameMutation.isPending || !renameValue.trim()}>
                            {renameMutation.isPending ? "Renaming..." : "Rename"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Merge Dialog */}
            <Dialog open={mergeDialogOpen} onOpenChange={setMergeDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Merge Document</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Select a document to merge &quot;{doc.title}&quot; into. The content will be appended to the target document, and this document will be deleted.
                        </p>
                        <div className="space-y-2">
                            <Label htmlFor="merge-target-compact">Target Document</Label>
                            <Select value={mergeTargetId} onValueChange={setMergeTargetId}>
                                <SelectTrigger id="merge-target-compact">
                                    <SelectValue placeholder="Select a document..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {documentsForMerge.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>{item.title ?? "Untitled"}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMergeDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirmMerge} disabled={mergeMutation.isPending || !mergeTargetId}>
                            {mergeMutation.isPending ? "Merging..." : "Merge"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Move to Folder Dialog */}
            <Dialog open={moveToFolderDialogOpen} onOpenChange={setMoveToFolderDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Move to Folder</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-muted-foreground">
                            Move &quot;{doc.title}&quot; to a folder.
                        </p>
                        <div className="space-y-2">
                            <Label>Folder</Label>
                            <Select
                                value={moveFolderTargetId ?? "none"}
                                onValueChange={(v) => setMoveFolderTargetId(v === "none" ? null : v)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a folder..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No folder (unfiled)</SelectItem>
                                    {folders.map((f) => (
                                        <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setMoveToFolderDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleConfirmMoveToFolder} disabled={moveToFolderMutation.isPending}>
                            {moveToFolderMutation.isPending ? "Moving..." : "Move"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

DocumentItem.displayName = "DocumentItem";
